/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import EmberObject, { set, get, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { later } from '@ember/runloop';
import { getTablePrintCss } from 'linced-app/utils/table';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import config from 'linced-app/config/environment';
import moment from 'moment';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { scaleOrdinal } from 'd3-scale';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AttendanceReportDialog extends Component {
  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  dates;

  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  strings;

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('academicYear.label', 'allTerms.@each.{academicYear,term}')
  get academicYearTerms() {
    return filterBy(
      this.allTerms,
      'academicYear',
      this.academicYear.label,
    ).sort(byKeys('term'));
  }

  @computed('strings.timetablePeriods', 'trackMovements')
  get periods() {
    if (this.trackMovements) {
      return 'days';
    }
    return this.strings.timetablePeriods;
  }

  @computed('attendanceCodes.allCodes.[]')
  get codeMap() {
    return this.attendanceCodes.allCodes.reduce((map, code) => {
      map[code.code] = code.label;
      return map;
    }, {});
  }

  @tracked
  reportLoading = true;
  @tracked
  reportData = null;
  @tracked
  summaryData = null;
  @tracked
  codeCountData = null;
  @tracked
  allRecords = [];
  @tracked
  allDayCodes = [];

  @computed('summaryData.terms.@each.term')
  get terms() {
    return this.get('summaryData.terms') ?? [];
  }

  @computed('allRecords.[]')
  get hasMorHCode() {
    return !!(
      findBy(this.allRecords, 'code', 'H') ||
      findBy(this.allRecords, 'code', 'M')
    );
  }

  @computed('codeCountData.terms.@each.term', 'attendanceCodes.allCodes.[]')
  get codeCountTerms() {
    let allData = [];
    let termsData = this.get('codeCountData.terms') ?? [];
    termsData.forEach((term) => {
      let data = [];
      rejectBy(this.attendanceCodes.allCodes, 'code', '?').forEach(
        function (code) {
          let key = code.code;
          if (term.learnerSummaryCodeCount) {
            let count = findBy(term.learnerSummaryCodeCount, 'code', key)
              ? findBy(term.learnerSummaryCodeCount, 'code', key).count
              : 0;
            if (count > 0) {
              data.push({
                value: key,
                label: code.label,
                count: count,
                tooltip: code.label,
              });
            }
          }
        },
      );
      allData.push({ term: term.term, data: data });
    });
    return allData;
  }

  @computed('codeCountTerms.[]')
  get ytdCodeCountMap() {
    let data = [];
    this.codeCountTerms.forEach((term) => {
      // find if code exists in data
      //loop over each code in term
      term.data.forEach((codeMap) => {
        let codeCount = findBy(data, 'label', codeMap.label);
        if (isEmpty(codeCount)) {
          data.push({
            value: codeMap.value,
            label: codeMap.label,
            count: codeMap.count,
            tooltip: codeMap.tooltip,
          });
        } else {
          codeCount.count += codeMap.count;
        }
      });
    });
    return data;
  }

  @tracked
  errorMessage = '';

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    let schoolId = this.get('session.activeSchool');
    let personId = this.get('person.id');
    let dateFrom = this.academicYear.start;
    let dayCodesPromise = Promise.resolve();
    let dateTo = moment().isBefore(moment(this.academicYear.end))
      ? moment().format('YYYY-MM-DD')
      : this.academicYear.end;
    this.reportLoading = true;
    this.errorMessage = '';
    this.filters = [];

    // Load absence records.
    let recordsPromise = this.store
      .query('attendance/record', {
        schoolId,
        personId,
        absencesOnly: true,
        dateFrom,
        dateTo,
        jurisdiction: this.jurisdiction,
      })
      .then((records) => {
        this.allRecords = records;
        if (this.trackMovements) {
          let presentCodes = mapBy(this.attendanceCodes.truancyPCodes, 'code');
          this.reportData = records
            .filter((record) => {
              return !presentCodes.includes(record.code);
            })
            .sort(byKeys('date', 'period.shortName'));
        } else {
          this.reportData = sortBy(records, 'date', 'period.shortName');
        }
      });

    if (this.trackMovements) {
      dayCodesPromise = this.store
        .query('attendance/nz-moe-day', {
          schoolId,
          personId,
          dateFrom,
          dateTo,
        })
        .then((dayCodes) => {
          this.allDayCodes = dayCodes;
        });
    }

    // Load attendance summaries for the relevant academic year.
    let startTime = moment(dateFrom, 'YYYY-MM-DD').unix();
    let endTime = moment(dateTo, 'YYYY-MM-DD').endOf('day').unix();
    let params = `schoolId=${schoolId}&personId=${personId}&startTime=${startTime}&endTime=${endTime}&jurisdiction=${this.jurisdiction}`;
    let url = `${config.APP.apiHost}/attendance/v4/learnersummary/nzmoe?${params}`;
    let summaryPromise = this.fetch.request(url).then((resp) => {
      this.summaryData = resp.summary;
    });
    let codeCountSummaryPromise = Promise.resolve();
    if (this.trackMovements) {
      url = `${config.APP.apiHost}/attendance/v4/learnersummary/codecount?${params}`;
      codeCountSummaryPromise = this.fetch.request(url).then((resp) => {
        this.codeCountData = resp.summary;
      });
    }
    // Load / calculate number of periods per term.
    let terms = {};
    this.academicYearTerms.forEach((term) => {
      // Coerce term number to a string so that it gets set correctly.
      set(terms, term.term, 0);
    });
    this.termTotals = terms;
    let ytdTermTotal = 0;
    let datesPromise = this.store
      .query('date', { from: dateFrom, to: dateTo })
      .then((dates) => {
        if (!isEmpty(dates)) {
          dates = sortBy(dates, 'date').reverse();

          let structure = this.dates.structureForDate(dates[0].date);

          dates.forEach((date) => {
            if (date.term) {
              // If we've gone beyond the start of the current timetable structure, find the previous one.
              if (structure.effectiveFrom < date.date) {
                structure = this.dates.structureForDate(date.date);
              }

              let day = parseInt(moment(date).format('E'), 10);
              let structureDay = findBy(
                structure.get('days'),
                'isoDayNumber',
                day,
              );
              let numPeriods = get(structureDay, 'periodIds.length') ?? 0;
              switch (date.status) {
                case SchoolStatus.OPEN:
                  ytdTermTotal += numPeriods;
                  set(terms, date.term, terms[date.term] + numPeriods);
                  break;
                case SchoolStatus.MORNING:
                case SchoolStatus.AFTERNOON:
                  // TODO: More advanced determination of number of periods on half-open days?
                  set(
                    terms,
                    date.term,
                    terms[date.term] + Math.floor(numPeriods / 2),
                  );
                  ytdTermTotal += Math.floor(numPeriods / 2);
                  break;
              }
            }
          });

          this.termTotals = terms;
          this.ytdTermTotal = ytdTermTotal;
        }
      });

    // Once we have all the relevant data, mark the report as loaded.
    Promise.all([
      recordsPromise,
      summaryPromise,
      datesPromise,
      codeCountSummaryPromise,
      dayCodesPromise,
    ])
      .catch(() => {
        this.errorMessage =
          'Something went wrong while generating this report.  Please try again later.';
      })
      .finally(() => {
        this.reportLoading = false;
      });
  }

  close() {}

  @computed('terms', 'ytdTermTotal')
  get ytdMap() {
    let map = {
      term: 'All',
      termTotal: 0,
      total: 0,
      totalPresent: 0,
      present: 0,
      absent: 0,
      late: 0,
      justified: 0,
      unjustified: 0,
    };
    this.terms.forEach((term) => {
      if (!isEmpty(term.total)) {
        map['total'] += term.total;
      }
      if (!isEmpty(term.present)) {
        map['present'] += term.present;
        map['totalPresent'] += term.present;
      }
      if (!isEmpty(term.late)) {
        map['totalPresent'] += term.late;
        map['late'] += term.late;
      }
      if (!isEmpty(term.justified)) {
        map['justified'] += term.justified;
        map['absent'] += term.justified;
      }
      if (!isEmpty(term.unjustified)) {
        map['absent'] += term.unjustified;
        map['unjustified'] += term.unjustified;
      }
    });
    return map;
  }

  @computed('allRecords.[]')
  get movementReportData() {
    let allRecords = [];
    let presentCode = this.get('attendanceCodes.present.code');
    this.allRecords.forEach((record) => {
      let movements = record.get('movements') ?? [];
      if (isEmpty(movements)) {
        if (record.code !== presentCode) {
          allRecords.push({
            code: record.code,
            notes: record.notes,
            author: record.author,
            fromTime: record.get('period.startTime'),
            date: record.date,
            dayName: record.dayName,
            term: record.dateObj,
          });
        }
      } else {
        movements = sortBy(movements, 'time');
        movements.forEach((movement, index) => {
          let toTime;
          if (movement.code !== presentCode) {
            let nextMovement = record.movements.objectAt(index + 1);
            if (!isEmpty(nextMovement)) {
              toTime = moment.unix(nextMovement.time).format('HH:mm');
            }
            allRecords.push({
              code: movement.code,
              notes: movement.notes,
              author: movement.author,
              fromTime: moment.unix(movement.time).format('HH:mm'),
              date: record.date,
              dayName: record.dayName,
              term: record.dateObj,
              toTime,
            });
          }
        });
      }
    });
    return allRecords;
  }

  @tracked
  filters = [];

  @tracked
  termTotals;

  @tracked
  ytdTermTotal;

  @computed('movementReportData.[]', 'filters.@each.{field,value}')
  get filteredMovementData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.movementReportData;
    }

    // Map raw filters into what we need to actually perform the filtering.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      return {
        key,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.movementReportData.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('academicYearTerms.[]', 'allDayCodes.[]', 'trackMovements')
  get byTruancyCode() {
    if (!this.trackMovements) {
      return {
        data: [],
        halfDaysMap: {},
      };
    }
    let data = [];
    let allDayCodes = this.allDayCodes || [];
    let allRecords = this.allRecords || [];
    let halfDaysMap = EmberObject.create({});
    let yearToDate = {};
    let scheme = this.getColourScheme();
    // For each term this academic year, build up data to be charted.
    this.academicYearTerms.forEach((term) => {
      // Initialize halfDaysMap for the current term if not already initialized
      if (!halfDaysMap[term.term]) {
        halfDaysMap[term.term] = {
          present: 0,
          absent: 0,
          total: 0,
          partialAbsences: 0,
          minorPartialAbsences: 0,
        };
      }
      if (!halfDaysMap['ytd']) {
        halfDaysMap['ytd'] = {
          present: 0,
          absent: 0,
          total: 0,
          partialAbsences: 0,
          minorPartialAbsences: 0,
        };
      }

      let termDayCodes = allDayCodes.filter((dayCode) => {
        return dayCode.date >= term.start && dayCode.date <= term.end;
      });
      let termRecords = allRecords.filter((record) => {
        return record.date >= term.start && record.date <= term.end;
      });

      let stats = this.attendance.calculateHalfDayAnalysis(
        termDayCodes,
        termRecords,
      );
      halfDaysMap[term.term].present = stats.present;
      halfDaysMap[term.term].absent = stats.absent;
      halfDaysMap[term.term].partialAbsences = stats.partialAbsences;
      halfDaysMap[term.term].minorPartialAbsences = stats.minorPartialAbsences;
      halfDaysMap[term.term].total = stats.total;

      halfDaysMap['ytd'].present += stats.present;
      halfDaysMap['ytd'].absent += stats.absent;
      halfDaysMap['ytd'].partialAbsences += stats.partialAbsences;
      halfDaysMap['ytd'].minorPartialAbsences += stats.minorPartialAbsences;
      halfDaysMap['ytd'].total += stats.total;

      let chartData = {};
      ['P', 'J', 'U', 'O'].forEach((key) => {
        let days = stats.counts[key] / 2;
        if (days > 0) {
          chartData[key] = days;
          yearToDate[key] = yearToDate[key] ? yearToDate[key] + days : days;
        }
      });

      // Flip map into a ready-to-chart array and push it into the array of terms to return.
      let termData = this.mapData(
        chartData,
        halfDaysMap[term.term].total,
        'truancy',
        scheme,
      );
      data.push({
        term: term.term,
        data: termData,
      });
    });
    let ytdData = this.mapData(
      yearToDate,
      halfDaysMap['ytd'].total,
      'truancy',
      scheme,
    );
    data.push({
      term: 'All',
      data: ytdData,
    });
    return { data, halfDaysMap };
  }

  @action
  handleSum(...operands) {
    let sum = this.sum(operands);
    return sum === 0 ? null : sum;
  }

  @action
  percentage(total, ...operands) {
    if (isEmpty(total)) {
      return null;
    }

    let sum = this.sum(operands);
    if (sum === 0) {
      return null;
    }
    let percentage = (sum / total) * 100;

    return percentage.toFixed(2) + '%';
  }

  @action
  printReport() {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );
    let content = document.querySelector(
      'md-dialog-content.individual-attendance-report',
    );

    printer.document.body.innerHTML = getTablePrintCss(false);

    printer.document.body.innerHTML += `<style>
      .layout-row {
        display: table-row;
      }
      .layout-column h2,
      .layout-column h3,
      .layout-column h4 {
        margin: 0;
      }
      .layout-row > div {
        display: table-cell;
      }
      .chart-pie .arc text, .chart-donut .arc text {
        font: 10px sans-serif;
        text-anchor: middle;
      }
      .chart-pie .hole-label, .chart-donut .hole-label {
        text-anchor: middle;
        font-weight: bold;
      }
    </style>`;

    printer.document.body.innerHTML += content.innerHTML;

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  sum(operands) {
    return operands.reduce((sum, operand) => {
      if (!isEmpty(operand)) {
        sum += operand;
      }
      return sum;
    }, 0);
  }

  getColourScheme() {
    return scaleOrdinal([
      '#d62728',
      '#ff9896',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf',
      '#aec7e8',
      '#ffbb78',
      '#98df8a',
      '#c5b0d5',
      '#c49c94',
      '#f7b6d2',
      '#c7c7c7',
      '#dbdb8d',
      '#9edae5',
    ]);
  }

  getLabel(code, type) {
    switch (type) {
      case 'truancy':
        return this.attendanceCodes.getLabelForTruancyCode(code);
      case 'raw':
        return get(
          findBy(this.attendanceCodes.allCodes, 'code', code),
          'label',
        );
    }

    return 'Unknown';
  }

  getColour(code, scheme) {
    switch (code) {
      case 'P':
        return '#2ca02c';
      case 'J':
        return '#ff7f0e';
      case 'U':
        return '#d62728';
    }

    // Fall back to colour scheme.
    return scheme(code);
  }

  mapData(rawData, total, type, scheme) {
    let tooltipMeasure = this.strings.timetablePeriods;
    if (this.trackMovements) {
      tooltipMeasure = 'days';
    }
    let data = [];
    Object.keys(rawData).forEach((code) => {
      let label = this.getLabel(code, type);
      let percentage = '';
      let count = rawData[code];
      if (count > 0 && total > 0) {
        percentage = ((count / total) * 100).toFixed(1) + '%';
      }

      // Handle pluralization for 'days'
      let displayMeasure = tooltipMeasure;
      if (tooltipMeasure === 'days' && count === 1) {
        displayMeasure = 'day';
      }

      data.push({
        value: code,
        count,
        tooltip: `${label} (${code}): ${percentage} (${count} ${displayMeasure})`,
        color: this.getColour(code, scheme),
      });
    });

    return data;
  }
}
